import { Database, Server, Cpu, HardDrive, Workflow, Cloud, Activity } from 'lucide-react';

export interface Component {
  x: number;
  y: number;
  label: string;
  type: 'input' | 'core' | 'worker' | 'data' | 'infra' | 'cloud';
  icon: any;
  port?: string;
  ports?: string;
  description?: string; // Detailed technical description
}

export interface Connection {
  from: string;
  to: string;
  type: string;
  style: 'solid' | 'dashed';
}

export interface ArchitectureConfig {
  components: Record<string, Component>;
  connections: Connection[];
}

// Global architecture configuration (complete system)
export const globalArchitectureConfig: ArchitectureConfig = {
  components: {
    // Input Sources (Left side - moved to top)
    echography: { x: 40, y: 30, label: 'Echography\nMachine', type: 'input', icon: Activity, description: 'The echography machine is the source of ultrasound images for the Deepecho platform.' },
    vscan: { x: 40, y: 100, label: 'Vscan\n(Third Party)', type: 'input', icon: Activity, description: 'Vscan is a third-party mobile application that connects to the Deepecho platform.' },
    butterfly: { x: 40, y: 170, label: 'Butterfly\nPACS', type: 'input', icon: Activity, description: 'Butterfly is a remote PACS system in the cloud that integrates with the Deepecho platform.' },
    deepecho: { x: 40, y: 240, label: 'Deepecho\nMobile App', type: 'input', icon: Activity, description: 'The Deepecho mobile app combines all web features plus mobile-specific features, including direct connection to ultrasound probes and other mobile capabilities.' },
    frontend: { x: 40, y: 310, label: 'Frontend', type: 'input', icon: Server, description: 'The Deepecho frontend is the web application for the Deepecho platform, providing the user interface for healthcare professionals.' },
    thirdPartyBackend: { x: 40, y: 380, label: 'Third Party\nBackend', type: 'input', icon: Server, description: 'Any third-party backend or service that uses the Deepecho API through the "API as Service" feature to integrate with the platform.' },

    // Core Services (Center-left - moved to top)
    pacsScp: { x: 250, y: 100, label: 'PACS-SCP', type: 'core', icon: Server, ports: '8085, 11109', description: 'The PACS-SCP is the Deepecho DICOM server that offers communication via the DICOM protocol, allowing it to receive ultrasound images from any source that supports the DICOM protocol.' },
    backend: { x: 250, y: 340, label: 'Backend', type: 'core', icon: Server, port: '8000', description: 'The core backend of the Deepecho platform, offering the REST API and business logic for all platform operations.' },
    arqWorker: { x: 900, y: 380, label: 'ARQ Worker', type: 'worker', icon: Cpu, description: 'The ARQ Worker is responsible for asynchronous tasks in the Deepecho platform. Its goal is to offload the backend by handling internal jobs such as storing files to GCS, performing calculations, and other background processing tasks.' },
    
    // Central Infrastructure (Center)
    redis: { x: 600, y: 280, label: 'Redis\n(Cache/Queue)', type: 'data', icon: Database, description: 'Redis is used as both a cache and a message queue for the Deepecho platform, providing fast data access and job queuing capabilities.' },
    rabbitmq: { x: 900, y: 80, label: 'RabbitMQ\n(Message Queue)', type: 'infra', icon: Workflow, description: 'RabbitMQ is used as the message broker for the Deepecho platform, enabling asynchronous communication between services through message queues.' },

    // Workers (Right side, horizontally aligned, moved up)
    videoWorker: { x: 1300, y: 0, label: 'Video\nConversion\nWorker', type: 'worker', icon: Cpu, description: 'The Video Worker is responsible for video conversion tasks in the Deepecho platform, processing and transforming video files as needed.' },
    // aiService: { x: 1400, y: 100, label: 'AI\nInferencing\nService', type: 'core', icon: Cpu },
    realtimeInferenceService: { x: 1450, y: 100, label: 'Realtime\nInference', type: 'core', icon: Cpu, description: 'The Realtime Inference Service handles real-time AI inference requests, processing ultrasound frames as they are captured during live examinations.' },
    batchInferenceService: { x: 1450, y: 200, label: 'Batch\nInference', type: 'core', icon: Cpu, description: 'The Batch Inference Service handles batch AI inference requests, processing multiple ultrasound images or videos in bulk for analysis and segmentation.' },
    blindSweepInferenceService: { x: 1450, y: 300, label: 'Blind Sweep\nInference', type: 'core', icon: Cpu, description: 'The Blind Sweep Inference Service is dedicated to processing blind sweep analyses, including heart rate detection, diabetes prediction, preeclampsia assessment, fetal presentation, placenta localization, and biometry measurements.' },
    // Data & Storage (Bottom)
    postgres: { x: 600, y: 620, label: 'PostgreSQL\n(Database)', type: 'data', icon: Database, description: 'PostgreSQL is the primary relational database for the Deepecho platform, storing all structured data including users, patients, exams, and metadata.' },
    arc: { x: 250, y: 620, label: 'ARC + LDAP\n(DICOM Archive)', type: 'infra', icon: HardDrive, description: 'ARC (Archive) combined with LDAP is used as the DICOM archive for the Deepecho platform, providing long-term storage and retrieval of DICOM studies.' },
    gcp: { x: 1200, y: 620, label: 'GCP\n(Storage + KMS + Secrets)', type: 'cloud', icon: Cloud, description: 'Google Cloud Platform services used by the platform (Storage buckets for files, Secret Manager for secrets, and KMS for cryptographic operations like JWT signing/verification).' },
    idp: { x: 1200, y: 420, label: 'IdP\n(SSO Provider)', type: 'cloud', icon: Cloud, description: 'External Identity Provider for SSO (SAML), e.g. Google Workspace / Okta / Azure AD.' },
    emailService: { x: 1200, y: 520, label: 'Email\nProvider', type: 'cloud', icon: Cloud, description: 'External email provider used to deliver invitation emails, set-up-account links, and verification codes (e.g. Resend/SMTP).' },
  },

  connections: [
    // DICOM connections to PACS-SCP (unidirectional: sources -> PACS-SCP)
    { from: 'echography', to: 'pacsScp', type: 'DICOM', style: 'solid' },
    { from: 'vscan', to: 'pacsScp', type: 'DICOM', style: 'solid' },
    { from: 'butterfly', to: 'pacsScp', type: 'DICOM', style: 'solid' },
    { from: 'deepecho', to: 'pacsScp', type: 'DICOM', style: 'dashed' },
    
    // HTTP connections (unidirectional: clients -> backend, backend -> services)
    { from: 'deepecho', to: 'backend', type: 'HTTP', style: 'solid' },
    { from: 'frontend', to: 'backend', type: 'HTTP', style: 'solid' },
    { from: 'thirdPartyBackend', to: 'backend', type: 'HTTP', style: 'solid' },
    { from: 'pacsScp', to: 'backend', type: 'HTTP', style: 'solid' },
    { from: 'backend', to: 'arc', type: 'HTTP', style: 'solid' },
    // Auth-related external services
    { from: 'backend', to: 'idp', type: 'HTTP', style: 'dashed' },
    { from: 'backend', to: 'emailService', type: 'HTTP', style: 'dashed' },
    
    // WebSocket connections (bidirectional: real-time communication)
    { from: 'frontend', to: 'backend', type: 'WebSocket', style: 'dashed' },
    { from: 'deepecho', to: 'backend', type: 'WebSocket', style: 'dashed' },
    // { from: 'arc', to: 'aiService', type: 'HTTP', style: 'solid' },
    { from: 'arc', to: 'batchInferenceService', type: 'HTTP', style: 'solid' },

    
    // Redis connections
    // Note: Backend and ARQ Worker communicate indirectly via Redis (backend enqueues jobs to Redis, ARQ Worker consumes from Redis)
    { from: 'pacsScp', to: 'redis', type: 'Redis', style: 'solid' },
    { from: 'backend', to: 'redis', type: 'Redis', style: 'solid' },
    { from: 'arqWorker', to: 'redis', type: 'Redis', style: 'solid' },
    { from: 'videoWorker', to: 'redis', type: 'Redis', style: 'solid' },
    // { from: 'aiService', to: 'redis', type: 'Redis', style: 'solid' },
    { from: 'batchInferenceService', to: 'redis', type: 'Redis', style: 'solid' },
    { from: 'blindSweepInferenceService', to: 'redis', type: 'Redis', style: 'solid' },
    { from: 'realtimeInferenceService', to: 'redis', type: 'Redis', style: 'solid' },

    
    // RabbitMQ connections (unidirectional: producers -> rabbitmq -> consumers)
    { from: 'pacsScp', to: 'rabbitmq', type: 'RabbitMQ', style: 'solid' },
    { from: 'backend', to: 'rabbitmq', type: 'RabbitMQ', style: 'solid' },
    { from: 'rabbitmq', to: 'arqWorker', type: 'RabbitMQ', style: 'solid' },
    { from: 'rabbitmq', to: 'videoWorker', type: 'RabbitMQ', style: 'solid' },
    // { from: 'rabbitmq', to: 'aiService', type: 'RabbitMQ', style: 'solid' },
    { from: 'rabbitmq', to: 'realtimeInferenceService', type: 'RabbitMQ', style: 'solid' },
    { from: 'rabbitmq', to: 'batchInferenceService', type: 'RabbitMQ', style: 'solid' },
    { from: 'rabbitmq', to: 'blindSweepInferenceService', type: 'RabbitMQ', style: 'solid' },
    
    // PostgreSQL connections
    { from: 'pacsScp', to: 'postgres', type: 'PostgreSQL', style: 'solid' },
    { from: 'backend', to: 'postgres', type: 'PostgreSQL', style: 'solid' },
    { from: 'arqWorker', to: 'postgres', type: 'PostgreSQL', style: 'solid' },
    { from: 'arc', to: 'postgres', type: 'PostgreSQL', style: 'solid' },
    
    // GCS connections
    { from: 'backend', to: 'gcp', type: 'GCS', style: 'solid' },
    { from: 'arqWorker', to: 'gcp', type: 'GCS', style: 'solid' },
    { from: 'videoWorker', to: 'gcp', type: 'GCS', style: 'solid' },
    // { from: 'aiService', to: 'gcp', type: 'GCS', style: 'solid' },
    { from: 'batchInferenceService', to: 'gcp', type: 'GCS', style: 'solid' },
    { from: 'blindSweepInferenceService', to: 'gcp', type: 'GCS', style: 'solid' },

    
  ],
};

